import i18n from '@/i18n'
import ApiService from '@/services/ApiService'
import Form from './Form';

export default class CodForm {

    constructor(data) {
        this.busy = false;
        this.successful = false;
        this.errors = {};
        this.rules = [];

        Object.assign(this, data);
    }

    /**
     * Start processing the form
     */
    startProcessing() {
        this.busy = true;
        this.successful = false;
        this.errors = {};
    }

    /**
     * Finish processing the form
     */
    finishProcessing() {
        this.busy = false;
        this.successful = true;
    }

    /**
     * Reset the errors and other state for the form
     */
    reset() {
        this.busy = false;
        this.successful = false;
        this.errors = {};
    }

    /**
     * Generate form data to send
     */
    toJson() {
        let jsonData = JSON.parse(JSON.stringify(this, function(k, v) {
            return v === undefined ? null : v;
        }));

        delete jsonData.errors;
        delete jsonData.successful;
        delete jsonData.busy;
        delete jsonData.rules;
        delete jsonData.valid;

        return jsonData;
    }

    /**
     * Helper method for making POST HTTP requests.
     */
    post(uri, config = null) {
        return this.send('post', uri, config);
    }

    /**
     * Helper method for making PUT HTTP requests.
     */
    put(uri, config = null) {
        return this.send('put', uri, config);
    }

    /**
     * Helper method for making PATCH HTTP requests.
     */
    patch(uri, config = null) {
        return this.send('patch', uri, config);
    }

    /**
     * Helper method for making DELETE HTTP requests.
     */
    delete(uri, config = null) {
        return this.send('delete', uri, config);
    }

    /**
     * Send the form to the back-end server.
     */
    send(method, uri, config = null) {
        return new Promise((resolve, reject) => {
            this.startProcessing();

            ApiService.http[method](uri, this.toJson(), config)
                .then(response => {
                    this.finishProcessing();
                    resolve(response);
                })
                .catch(errors => {
                    this.busy = false;
                    if(errors.response && errors.response.data.errors) {
                        this.errors = errors.response.data.errors;
                    }

                    reject(errors);
                });
        });
    }

    static rule(rule, ...args) {
        let emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

        return v => {

            const exists = !!v || v === 0;

            switch(rule) {
                case 'required':
                    return exists || args[0] || i18n.t('validation.required')
                case 'required_if':
                    return exists || !args[0]() || args[1] || i18n.t('validation.required')
                case 'email':
                    return (exists && emailRegex.test(v.trim())) || args[0] || i18n.t('validation.email')
                case 'password':
                    return (exists && v.length >= 8) || args[0] || i18n.t('validation.password')
                case 'pin':
                    return (exists && v.length >= 4) || args[0] || i18n.t('validation.pin')
                case 'min_length':
                    return (!exists || v.length >= args[0]) || args[1] || i18n.t('validation.min_length', { value: args[0] })
                case 'max_length':
                    return (!exists || v.length <= args[0]) || args[1] || i18n.t('validation.max_length', { value: args[0] })
                case 'min_value':
                    return (exists && v >= args[0]) || args[1] || i18n.t('validation.min_value', { value: args[0] })
                case 'max_value':
                    return (exists && v <= args[0]) || args[1] || i18n.t('validation.max_value', { value: args[0] })
                case 'between':
                    return (exists && v >= args[0] && v <= args[1]) || args[2] || i18n.t('validation.between', { min: args[0], max: args[1] })
                case 'match':
                    return ((!exists && !args[0]()) || v === args[0]()) || args[1] || i18n.t('validation.same_as')
                case 'unique':
                    return (exists && !args[0]().includes(v)) || args[1] || i18n.t('validation.unique')
                case 'diff':
                    return (exists && v !== args[0]()) || args[1] || i18n.t('validation.diff')
                case 'numeric':
                    return (!exists || /^[0-9]+$/.test(v)) || args[0] || i18n.t('validation.numeric')

                default:
                    return true
            }
        }
    }

    /**
     * Encode the file coming from file input with base64
     * and set encoded value to the form field
     */
    async encodeFile(field, file) {
        const base64File = await Form.fileToBase64(file);
        this[field] = base64File || null;
    }

    /**
     * Encode the file coming from file input with base64
     */
    static fileToBase64(file) {
        return new Promise((resolve, reject) => {
            if(!file) resolve(null);

            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = () => {
                resolve(reader.result)
            };
        });
    }
}
